import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BoxPage } from '@/components/common'
import { Card } from '@/@core/components/mui/Card'
import { CardContent, IconButton, Typography, Box, Divider, Tab, Tabs, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { useNotification } from '@/hooks/useNotification'
import {
  Company,
  CompanyStatusEnum,
  ICompany,
  useCompanyStatusMutation,
  User,
  UserTypeEnum,
  ActivityLogTypeEnum,
  ClosingDateTypeEnum,
  PaymentPartnerEnum
} from '@/@data'
import { useTranslator } from '@/hooks/useTranslator'
import { getInitials } from '@/utils/getInitials'
import { EmployerStatusDropDown } from '@/components/employer/EmployerStatusDropDown'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { TabButton } from '@/components/common/button/TabButton'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import AppLogs from '@/components/common/AppLogs'
import {
  EmployerActiveContract,
  EmployerDetailTable,
  EmployerDetailTableFilter,
  EmployerNote,
  EmployerSetting,
  EmployerContractHistory
} from '@/components/employer'

const statusBgColor: Record<string, string> = {
  active: '#D4F4E2',
  pending: '#FFECD9',
  disable: '#EAEAEC',
  guest: '#DFEBFF',
  blocked: '#FFDBDC',
  guest_waiting: '#DFEBFF'
}

const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
  const { logo_file, name } = params

  if (logo_file) {
    return <CustomAvatar src={logo_file.tiny_url} skin='light' size={80} />
  } else {
    return (
      <CustomAvatar skin='light' size={80}>
        {getInitials(name as string)}
      </CustomAvatar>
    )
  }
}

// Custom Tab Component

const EmployerDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { __ } = useTranslator()
  const n = useNotification()
  const [tabValue, setTabValue] = useState(0)
  const [contractTabValue, setContractTabValue] = useState(0)
  const { searchQuery } = useRouteQuery()
  const { serializeQuery } = useRouteQuery()
  const [openSetting, setOpenSetting] = useState(false)
  const [isOpenLogs, setIsOpenLogs] = useState(false)

  const { data: employer, refetch } = useQuery({
    ...Company.objects.findOneQueryFactory(Number(id)),
    enabled: !!id
  })

  const searchParams = useMemo(
    () => ({
      ...serializeQuery({
        page: searchQuery.get('page'),
        perPage: searchQuery.get('perPage'),
        keywords: searchQuery.get('keywords'),
        company_id: Number(id),
        birth_date: searchQuery.get('birth_date'),
        status: searchQuery.get('status'),
        user_type: UserTypeEnum.User
      })
    }),
    [searchQuery]
  )

  const { refetch: refetchUsers, data: employees } = useQuery({
    ...User.objects.paginateQueryFactory(searchParams)
  })

  const { mutate: updateCompanyStatus } = useCompanyStatusMutation()

  if (!employer) return null

  const tabs = [
    {
      label: __('Employees'),
      icon: <i className='tabler-users text-[18px]' />
    },
    {
      label: __('Active Contract'),
      icon: <i className='tabler-link text-[18px]' />
    },
    {
      label: __('Note'),
      icon: <i className='tabler-note text-[18px]' />
    }
  ]

  return (
    <>
      <BoxPage
        showBackButton={true}
        title={__(` ${openSetting ? ` ${employer.name} Detail` : 'Employer Details'}`)}
        breadcrumbs={[{ label: __('Employer Management'), href: '/cms/employer' }, { label: __('Employer Details') }]}
      >
        {openSetting ? (
          <EmployerSetting onClose={() => setOpenSetting(false)} employer={employer} refetch={refetch} />
        ) : (
          <div>
            <Card
              sx={{
                borderRadius: 3,
                mb: 6,
                backgroundColor: statusBgColor[employer.status?.toLowerCase()] || '#F9F9F9'
              }}
            >
              <CardContent>
                <Box className='flex items-star justify-between mb-4'>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3 }} className='items-start'>
                    <div className='border rounded-full'>
                      {getAvatar({ logo_file: employer?.logo_file, name: employer?.name || '-' })}
                    </div>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Tooltip title={employer.name}>
                          <Typography
                            variant='h5'
                            gutterBottom
                            sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}
                            className='max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis'
                          >
                            {employer.name}
                          </Typography>
                        </Tooltip>
                        <EmployerStatusDropDown
                          status={employer.status as CompanyStatusEnum}
                          onSelect={status => {
                            updateCompanyStatus(
                              {
                                id: employer.id,
                                data: {
                                  status: status as CompanyStatusEnum
                                }
                              },
                              {
                                onSuccess: () => {
                                  refetch()
                                  n.success(__('Status updated successfully'))
                                },
                                onError: () => {
                                  n.error(__('Status update failed'))
                                }
                              }
                            )
                          }}
                          sx={{
                            height: '30px',
                            fontSize: '13px'
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className='tabler-id text-[18px] text-[#2f2b3d66]' />
                        <span>{employer?.system_code}</span>
                        <IconButton
                          size='small'
                          sx={{
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: '#e0e0e0'
                            }
                          }}
                          onClick={() => {
                            navigator.clipboard.writeText(employer.system_code || '')
                            n.success(__('Copied to clipboard!'))
                          }}
                        >
                          <i className='tabler-copy text-[#2f2b3db3]' />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => setOpenSetting(true)}
                    >
                      <i className='tabler-settings text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => setIsOpenLogs(true)}
                    >
                      <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Card className='shadow-none border rounded-[16px] bg-[#F9F9F9] px-5 py-6'>
                  <Typography className='text-[18px] font-medium text-[#2f2b3de6]'>{__('Employer Info')}</Typography>
                  <Divider sx={{ my: 4 }} />

                  <div className='grid gap-5'>
                    <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Full Name')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          {employer.name}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Short Name')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          <Tooltip title={employer.short_name || '-'}>
                            <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                              {employer.short_name || '-'}
                            </Typography>
                          </Tooltip>
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Business Registration')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          {employer?.business_number || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Type')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          {employer?.company_type || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('POC Name')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          {employer?.poc_firstname || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('POC Phone')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {(employer?.poc_phone_code && `(${employer?.poc_phone_code}) `) || '(+84)'}{' '}
                          {employer?.poc_phone || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('POC Email')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium whitespace-nowrap overflow-hidden text-ellipsis'>
                          {employer?.email || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Establish Date')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {employer?.establish_date ? dayjs(employer?.establish_date).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Bank Name')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {employer?.bank?.shortName || '-'} - {employer?.bank?.name || '-'}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Bank Account')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {employer?.bank_account_number || '-'}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Minimum Working Days')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {Number(employer?.minimum_working_days) || '0'} {__('working days')}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Timesheet Closing Date')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {Number(employer?.closing_date) === 0
                            ? __('End of Month')
                            : Number(employer?.closing_date) === 1
                              ? __('First of Month')
                              : Number(employer?.closing_date) || '-'}{' '}
                          {employer?.closing_date !== 1 && employer?.closing_date !== 0 && (
                            <>
                              -{' '}
                              {employer?.closing_date_type === ClosingDateTypeEnum.Before
                                ? __('Previous Month')
                                : __('Next Month')}
                            </>
                          )}
                          {/* {employer?.closing_date_type === ClosingDateTypeEnum.Before
                            ? __('Previous Month')
                            : __('Next Month')} */}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Payment Partner')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {employer?.payment_partner === PaymentPartnerEnum.Shinhan ? 'Shinhan Bank' : 'VNPT ePay'}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Employer Address')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {employer?.address || '-'}
                        </Typography>
                      </Box>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px] font-normal'>
                          {__('Withdraw Cutoff Day')}:
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px] font-medium'>
                          {Number(employer?.withdraw_cutoff_day || 0) === 0
                            ? __('End of Month')
                            : Number(employer?.withdraw_cutoff_day || 0)}
                        </Typography>
                      </Box>
                    </div>
                  </div>
                </Card>
              </CardContent>
            </Card>

            {/* Custom Tabs */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
              {tabs.map((tab, index) => (
                <TabButton
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  isSelected={tabValue === index}
                  onClick={() => setTabValue(index)}
                />
              ))}
            </Box>

            <Card
              sx={{
                borderRadius: 3
              }}
            >
              <CardContent sx={{ padding: 0 }}>
                {tabValue === 0 && (
                  <>
                    <EmployerDetailTableFilter refetch={refetchUsers} businessNumber={employer.business_number} />
                    <EmployerDetailTable
                      data={employees?.data || []}
                      meta={employees?.meta}
                      refetch={refetchUsers}
                      isDetail={true}
                    />
                  </>
                )}
                {tabValue === 1 && (
                  <>
                    <Box className='px-6 pt-6'>
                      <Tabs
                        value={contractTabValue}
                        onChange={(_, newValue) => setContractTabValue(newValue)}
                        aria-label='contract tabs'
                      >
                        <Tab label={__('Active Contract')} />
                        <Tab label={__('Contract History')} />
                      </Tabs>
                    </Box>

                    {contractTabValue === 0 && <EmployerActiveContract />}
                    {contractTabValue === 1 && <EmployerContractHistory />}
                  </>
                )}
                {tabValue === 2 && <EmployerNote />}
              </CardContent>
            </Card>
          </div>
        )}
      </BoxPage>

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Employer Detail')}
        object_type={ActivityLogTypeEnum.Company}
        objectId={employer.id}
      />
    </>
  )
}

export default EmployerDetail
